/**
 * Hook to handle SOV (Schedule of Values) line item initialization for billing.
 * Extracts all initialization logic from BillingApplicationPage for readability.
 */

// Helper to round to 2 decimal places for all monetary calculations
const round2 = (num) => {
  const val = Math.round((num || 0) * 100) / 100;
  return isFinite(val) ? val : 0;
};

/**
 * Filter out manual CO line items for COs that are no longer approved
 */
export const filterUnapprovedManualCOs = (items, jobData) => {
  const changeOrders = jobData?.change_orders || [];
  const approvedManualCONums = new Set(
    changeOrders
      .filter(co => co.date_approved && !co.source_bid_sheet_id)
      .map(co => co.co_number)
  );
  return items.filter(item => {
    const match = item.description?.match(/^CO #(\S+) - (Labor|Material|Equipment)$/);
    if (!match) return true;
    const coNum = match[1];
    const coForItem = changeOrders.find(co => co.co_number === coNum);
    if (!coForItem) return true;
    if (coForItem.source_bid_sheet_id) return true;
    return approvedManualCONums.has(coNum);
  });
};

/**
 * Helper to add manual change orders (no linked bid sheet) to line items
 */
const appendManualChangeOrders = (jobData, items, itemIndex, previousTotalsMap = {}) => {
  const changeOrders = jobData?.change_orders || [];
  const bidSheets = jobData?.bid_sheets || [];
  
  const existingDescriptions = new Set(items.map(i => i.description));
  
  changeOrders.forEach((co) => {
    if (!co.date_approved) return;
    
    if (co.source_bid_sheet_id) {
      const linkedSheet = bidSheets.find(bs => bs.id === co.source_bid_sheet_id);
      if (linkedSheet) return;
    }
    
    const coNum = co.co_number || "CO";
    const coPrefix = `CO #${coNum} - `;
    
    const alreadyExists = existingDescriptions.has(`${coPrefix}Labor`) || 
                          existingDescriptions.has(`${coPrefix}Material`) || 
                          existingDescriptions.has(`${coPrefix}Equipment`);
    if (alreadyExists) return;
    
    const labor = parseFloat(co.labor_amount) || 0;
    const material = parseFloat(co.material_amount) || 0;
    const equipment = parseFloat(co.equipment_amount) || 0;
    
    if (labor !== 0) {
      const itemId = `co-labor-${co.id}`;
      items.push({
        id: itemId,
        item_number: itemIndex++,
        description: `${coPrefix}Labor`,
        scheduled_value: round2(labor),
        from_previous: round2(previousTotalsMap[itemId] || 0),
        this_period_amount: 0,
        this_period_percent: 0,
        material_stored: 0,
        source_type: "labor",
        source_category: "change_order",
        is_change_order: true,
      });
    }
    if (material !== 0) {
      const itemId = `co-material-${co.id}`;
      items.push({
        id: itemId,
        item_number: itemIndex++,
        description: `${coPrefix}Material`,
        scheduled_value: round2(material),
        from_previous: round2(previousTotalsMap[itemId] || 0),
        this_period_amount: 0,
        this_period_percent: 0,
        material_stored: 0,
        source_type: "material",
        source_category: "change_order",
        is_change_order: true,
      });
    }
    if (equipment !== 0) {
      const itemId = `co-equipment-${co.id}`;
      items.push({
        id: itemId,
        item_number: itemIndex++,
        description: `${coPrefix}Equipment`,
        scheduled_value: round2(equipment),
        from_previous: round2(previousTotalsMap[itemId] || 0),
        this_period_amount: 0,
        this_period_percent: 0,
        material_stored: 0,
        source_type: "equipment",
        source_category: "change_order",
        is_change_order: true,
      });
    }
  });
  
  return itemIndex;
};

/**
 * Process a single bid sheet into SOV line items
 */
const processSheetToLineItems = (sheet, items, itemIndex, isChangeOrder, previousTotalsMap = {}, options = {}) => {
  if (!sheet) return itemIndex;
  
  const { lookup = { materials: {}, misc: {} }, prefixSheetName = false } = options;
  const sheetName = sheet.name || "Base Bid";
  const coPrefix = isChangeOrder
    ? sheetName.replace("Change Order", "CO").replace("#", "#") + " - "
    : (prefixSheetName ? `${sheetName} - ` : "");
  
  const materialItems = sheet.material_items || [];
  const laborEntries = sheet.labor_entries || [];
  const equipmentItems = sheet.equipment_items || [];
  const miscItems = sheet.misc_items || [];

  const calcMaterialSubtotal = materialItems.reduce((sum, item) => sum + (item.total || 0), 0);
  const calcMaterialMortarSubtotal = materialItems.reduce((sum, item) => sum + (item.mortar_total || 0), 0);
  const calcLaborFromMaterials = materialItems.reduce((sum, item) => sum + (item.labor_total || 0), 0);
  const calcStandaloneLabor = laborEntries
    .filter((e) => !e.is_material_labor && !e.is_equipment_labor && !e.is_misc_labor)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const calcEquipmentLabor = laborEntries
    .filter((e) => e.is_equipment_labor)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const calcMiscLabor = laborEntries
    .filter((e) => e.is_misc_labor)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const calcEquipmentSubtotal = equipmentItems.reduce((sum, item) => sum + (item.total || 0), 0);
  const calcMiscLaborFromEntries = calcMiscLabor;

  const grandTotal = sheet.grand_total || 0;

  const lineItemBases = [];

  // Group materials by Billing Category (from the master material) when coded,
  // otherwise fall back to the material category.
  const materialsByCategory = {};
  materialItems.forEach((item) => {
    const cat = item.billing_category_override || (lookup.materials || {})[item.item_id] || item.category || "Materials";
    if (!materialsByCategory[cat]) materialsByCategory[cat] = [];
    materialsByCategory[cat].push(item);
  });

  Object.entries(materialsByCategory).forEach(([category, catItems]) => {
    const catMaterialTotal = catItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const catMortarTotal = catItems.reduce((sum, item) => sum + (item.mortar_total || 0), 0);
    const catLaborTotal = catItems.reduce((sum, item) => sum + (item.labor_total || 0), 0);

    if (catMaterialTotal + catMortarTotal !== 0) {
      lineItemBases.push({
        type: "material",
        category: category,
        description: `${coPrefix}${category} Material`,
        baseCost: catMaterialTotal + catMortarTotal
      });
    }
    if (catLaborTotal !== 0) {
      lineItemBases.push({
        type: "labor",
        category: category,
        description: `${coPrefix}${category} Labor`,
        baseCost: catLaborTotal
      });
    }
  });

  if (calcStandaloneLabor !== 0) {
    lineItemBases.push({
      type: "labor",
      category: "standalone",
      description: `${coPrefix}Labor`,
      baseCost: calcStandaloneLabor
    });
  }

  // Equipment + Equipment Labor collapse into ONE "Equipment" line.
  if (calcEquipmentSubtotal + calcEquipmentLabor !== 0) {
    lineItemBases.push({
      type: "equipment",
      category: "all",
      description: `${coPrefix}Equipment`,
      baseCost: calcEquipmentSubtotal + calcEquipmentLabor
    });
  }

  // Misc items coded to a Billing Category join that category's Material/Labor lines;
  // uncoded misc items keep today's "Miscellaneous" / "Misc Labor" lines.
  const codedMisc = {};
  let uncodedMiscMaterial = 0;
  let uncodedMiscLabor = 0;
  miscItems.forEach((item) => {
    const material = (item.total || 0) + (item.material_cost || 0) + (item.mortar_cost || 0);
    const labor = item.labor_cost || 0;
    const cat = item.billing_category_override
      || (lookup.misc || {})[item.source_misc_id]
      || (lookup.miscByDescription || {})[(item.description || "").trim().toLowerCase()];
    if (cat) {
      if (!codedMisc[cat]) codedMisc[cat] = { material: 0, labor: 0 };
      codedMisc[cat].material += material;
      codedMisc[cat].labor += labor;
    } else {
      uncodedMiscMaterial += material;
      uncodedMiscLabor += labor;
    }
  });
  // Misc labor entries (from labor_entries) are not itemized per misc item -> stay in Misc Labor
  uncodedMiscLabor += calcMiscLaborFromEntries - miscItems.reduce((sum, i) => sum + (i.labor_cost || 0), 0);

  Object.entries(codedMisc).forEach(([category, totals]) => {
    const existingMaterial = lineItemBases.find((b) => b.type === "material" && b.category === category);
    const existingLabor = lineItemBases.find((b) => b.type === "labor" && b.category === category);
    if (totals.material !== 0) {
      if (existingMaterial) existingMaterial.baseCost += totals.material;
      else lineItemBases.push({ type: "material", category, description: `${coPrefix}${category} Material`, baseCost: totals.material });
    }
    if (totals.labor !== 0) {
      if (existingLabor) existingLabor.baseCost += totals.labor;
      else lineItemBases.push({ type: "labor", category, description: `${coPrefix}${category} Labor`, baseCost: totals.labor });
    }
  });

  if (uncodedMiscMaterial !== 0) {
    lineItemBases.push({
      type: "misc",
      category: "material",
      description: `${coPrefix}Miscellaneous`,
      baseCost: uncodedMiscMaterial
    });
  }
  if (uncodedMiscLabor !== 0) {
    lineItemBases.push({
      type: "labor",
      category: "misc",
      description: `${coPrefix}Misc Labor`,
      baseCost: uncodedMiscLabor
    });
  }

  const actualTotalBaseCost = lineItemBases.reduce((sum, item) => sum + Math.abs(item.baseCost), 0);

  lineItemBases.forEach((lineItem) => {
    const scheduledValue = actualTotalBaseCost !== 0 
      ? (Math.abs(lineItem.baseCost) / actualTotalBaseCost) * grandTotal
      : lineItem.baseCost;
    
    const itemId = `${lineItem.type}-${lineItem.category || 'all'}-${sheet.id}-${Date.now()}-${itemIndex}`;
    
    items.push({
      id: itemId,
      item_number: itemIndex++,
      description: lineItem.description,
      scheduled_value: Math.round(scheduledValue * 100) / 100,
      from_previous: round2(previousTotalsMap[itemId] || 0),
      this_period_amount: 0,
      this_period_percent: 0,
      material_stored: 0,
      source_type: lineItem.type,
      source_category: lineItem.category,
      source_sheet_id: sheet.id,
      source_sheet_name: sheetName,
      is_change_order: isChangeOrder,
    });
  });

  return itemIndex;
};

/**
 * Append newly approved COs that aren't already in the SOV
 */
const appendNewlyApprovedCOs = (jobData, existingItems, previousTotalsMap) => {
  const bidSheets = jobData?.bid_sheets || [];
  
  const approvedCOs = bidSheets.filter((sheet) => 
    sheet?.bid_type === "change_order" && sheet?.co_status === "approved"
  );
  
  const existingCONumbers = new Set();
  existingItems.forEach(item => {
    const match = item.description?.match(/CO #(\d+)/);
    if (match) {
      existingCONumbers.add(match[1]);
    }
  });
  
  const newCOs = approvedCOs.filter(sheet => {
    const coNumber = sheet.name?.replace("Change Order #", "").trim();
    return !existingCONumbers.has(coNumber);
  });
  
  const newItems = [...existingItems];
  let itemIndex = newItems.length + 1;
  
  const sheetOptions = { lookup: jobData?._billingLookup || { materials: {}, misc: {} } };
  newCOs.forEach((sheet) => {
    itemIndex = processSheetToLineItems(sheet, newItems, itemIndex, true, previousTotalsMap, sheetOptions);
  });
  
  appendManualChangeOrders(jobData, newItems, itemIndex, previousTotalsMap);
  
  if (newItems.length === existingItems.length) return existingItems;
  
  return newItems.map((item, index) => ({
    ...item,
    item_number: index + 1
  }));
};

/**
 * Calculate cumulative totals from previous billings
 */
const buildPreviousTotalsMap = (allBillings) => {
  const previousTotalsMap = {};
  allBillings.forEach((billing) => {
    (billing?.line_items || []).forEach((item) => {
      const key = item.id;
      if (!previousTotalsMap[key]) previousTotalsMap[key] = 0;
      previousTotalsMap[key] += round2((item.this_period_amount || 0) + (item.material_stored || 0));
    });
  });
  return previousTotalsMap;
};

/**
 * Initialize from official SOV
 */
const initializeFromOfficialSOV = (jobData, officialSOV, existingBilling) => {
  const allBillings = (jobData?.billings || []).filter(
    (b) => (!existingBilling || b.id !== existingBilling?.id) && b.status !== "void"
  );
  const previousTotalsMap = buildPreviousTotalsMap(allBillings);

  let items = officialSOV.map((item, index) => ({
    ...item,
    item_number: index + 1,
    scheduled_value: round2(item.scheduled_value),
    from_previous: round2(previousTotalsMap[item.id] || 0),
    this_period_amount: 0,
    this_period_percent: 0,
    material_stored: 0,
  }));

  items = appendNewlyApprovedCOs(jobData, items, previousTotalsMap);
  return filterUnapprovedManualCOs(items, jobData);
};

/**
 * Initialize from previous billing's schedule of values
 */
const initializeFromPreviousBilling = (jobData, previousBilling, existingBilling) => {
  const allBillings = (jobData?.billings || []).filter(
    (b) => (!existingBilling || b.id !== existingBilling?.id) && b.status !== "void"
  );
  const previousTotalsMap = buildPreviousTotalsMap(allBillings);

  let items = previousBilling.line_items.map((item, index) => ({
    ...item,
    item_number: index + 1,
    scheduled_value: round2(item.scheduled_value),
    from_previous: round2(previousTotalsMap[item.id] || 0),
    this_period_amount: 0,
    this_period_percent: 0,
    material_stored: 0,
  }));

  items = appendNewlyApprovedCOs(jobData, items, previousTotalsMap);
  return filterUnapprovedManualCOs(items, jobData);
};

/**
 * Initialize from bid sheets (first billing only)
 */
const initializeFromBidSheets = (jobData) => {
  const items = [];
  const bidSheets = jobData?.bid_sheets || [];
  const acceptedBidIds = jobData?.accepted_bid_ids || [];

  let itemIndex = 1;

  const baseBids = bidSheets.filter((sheet) => {
    if (sheet.bid_type === "change_order") return false;
    if (acceptedBidIds.length > 0) {
      return acceptedBidIds.includes(sheet.id);
    }
    // Fallback: exclude auto-generated "Lump Sum Bid" if there are other base bids
    if (sheet.name === "Lump Sum Bid") {
      const hasOtherBaseBids = bidSheets.some(s => s.bid_type === "base_bid" && s.name !== "Lump Sum Bid");
      if (hasOtherBaseBids) return false;
    }
    return sheet.bid_type === "base_bid";
  });
  
  const changeOrderBids = bidSheets.filter((sheet) => 
    sheet.bid_type === "change_order" && sheet.co_status === "approved"
  );

  const sheetOptions = {
    lookup: jobData?._billingLookup || { materials: {}, misc: {} },
    prefixSheetName: baseBids.length > 1,
  };

  baseBids.forEach((sheet) => {
    itemIndex = processSheetToLineItems(sheet, items, itemIndex, false, {}, sheetOptions);
  });

  changeOrderBids.forEach((sheet) => {
    itemIndex = processSheetToLineItems(sheet, items, itemIndex, true, {}, sheetOptions);
  });

  appendManualChangeOrders(jobData, items, itemIndex);
  return filterUnapprovedManualCOs(items, jobData);
};

/**
 * Main initialization entry point — determines which strategy to use
 * @returns {Array} Initialized line items
 */
export const initializeLineItems = (jobData, existingBilling) => {
  const existingBillings = jobData?.billings || [];
  const officialSOV = jobData?.official_sov;
  
  const sortedBillings = [...existingBillings]
    .filter((b) => (!existingBilling || b.id !== existingBilling?.id) && b.status !== "void")
    .sort((a, b) => {
      const bNum = parseInt(String(b.billing_number || "0").split("-")[0] || "0") || 0;
      const aNum = parseInt(String(a.billing_number || "0").split("-")[0] || "0") || 0;
      return bNum - aNum;
    });
  
  const mostRecentBilling = sortedBillings[0];
  
  if (mostRecentBilling?.line_items?.length > 0) {
    return initializeFromPreviousBilling(jobData, mostRecentBilling, existingBilling);
  } else if (officialSOV?.length > 0) {
    return initializeFromOfficialSOV(jobData, officialSOV, existingBilling);
  } else {
    return initializeFromBidSheets(jobData);
  }
};

/** Lists bid-sheet lines that have no billing category (master or override) before the first SOV is built. */
export const collectUncodedBidItems = (job) => {
  if (!job?.bid_sheets?.length) return [];
  const lookup = job._billingLookup || { materials: {}, misc: {}, miscByDescription: {} };
  const acceptedIds = job.accepted_bid_ids || [];
  const sheets = job.bid_sheets.filter((s) => {
    if (s.bid_type === "change_order") return s.co_status === "approved";
    if (acceptedIds.length) return acceptedIds.includes(s.id);
    if (s.name === "Lump Sum Bid" && job.bid_sheets.some((o) => o.bid_type === "base_bid" && o.name !== "Lump Sum Bid")) return false;
    return s.bid_type === "base_bid";
  });
  const out = [];
  sheets.forEach((sheet) => {
    (sheet.material_items || []).forEach((it) => {
      if (!it.billing_category_override && !(lookup.materials || {})[it.item_id]) {
        out.push({ key: `m-${sheet.id}-${it.id}`, sheet: sheet.name || "Base Bid", kind: "Material", name: it.item_name || it.description || "Material", fallback: it.category || "Materials" });
      }
    });
    (sheet.misc_items || []).forEach((it) => {
      const byDesc = (lookup.miscByDescription || {})[(it.description || "").trim().toLowerCase()];
      if (!it.billing_category_override && !(lookup.misc || {})[it.source_misc_id] && !byDesc) {
        out.push({ key: `x-${sheet.id}-${it.id}`, sheet: sheet.name || "Base Bid", kind: "Misc", name: it.description || "Misc item", fallback: "Miscellaneous" });
      }
    });
  });
  return out;
};

export { round2, initializeFromBidSheets };
